// Groove: quantize with strength and swing — Live's "Quantize Settings"
// dialog, in the piano roll. Pure over wire notes, like every roll tool.
//
// Strength moves a note only PART of the way to its grid point (50% keeps
// half of the player's timing); swing delays every second grid step by a
// fraction of the grid (at 1/8 grid, 50% swing is the classic 2:1 shuffle,
// the off-beat landing two thirds of the way through the pair). Both are
// timing-only: pitch, velocity and length are untouched, and a note never
// leaves the clip.

import type { WireNote } from "@shared/midi/notes.js";
import { MIN_NOTE_BEATS, noteKey } from "@web-shared/lib/piano-roll";

export interface Groove {
  /** 0..1 — how far each note moves toward its target. */
  strength: number;
  /** 0..1 — how far the off-beat is pushed late, as a fraction of the grid
   * (0.5 ⇒ the off-beat sits two thirds of the way through the pair). */
  swing: number;
}

/** The swung grid point nearest `beat`: the even steps stay, the odd ones
 * sit `swing` of a grid later. */
export function swungGridPoint(beat: number, grid: number, swing: number): number {
  const pair = grid * 2;
  const base = Math.floor(beat / pair) * pair;
  const candidates = [base, base + grid + grid * swing, base + pair, base + pair + grid + grid * swing];
  let best = candidates[0]!;
  for (const c of candidates) if (Math.abs(c - beat) < Math.abs(best - beat)) best = c;
  return best;
}

/**
 * Quantize the keyed notes (every note when `keys` is empty) to `grid` with
 * strength and swing. Rounded to 1e-4 beats so a partial move is a clean
 * wire value.
 */
export function grooveNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  grid: number,
  groove: Groove,
  clipDuration: number,
): WireNote[] {
  if (!(grid > 0)) return [...notes];
  const strength = Math.max(0, Math.min(1, groove.strength));
  const swing = Math.max(0, Math.min(1, groove.swing));
  if (strength === 0) return [...notes];
  const all = keys.size === 0;
  const ceiling = Math.max(0, clipDuration - MIN_NOTE_BEATS);
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const target = swing > 0 ? swungGridPoint(n.startTime, grid, swing) : Math.round(n.startTime / grid) * grid;
    const moved = n.startTime + (target - n.startTime) * strength;
    const start = Math.round(Math.min(Math.max(0, moved), ceiling) * 1e4) / 1e4;
    return start === n.startTime ? n : { ...n, startTime: start };
  });
}
